import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    User,
    Mail,
    Phone,
    Building2,
    MapPin,
    Car,
    Home,
    CheckCircle,
    XCircle,
    Clock,
    Calendar,
    FileText,
    Download,
    Eye,
    Store,
    TrendingUp,
    AlertCircle,
    X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
    useGetDealerDetailsQuery,
    useGetDealerDocumentsQuery,
    useVerifyDealerMutation,
    useUpdateDealerStatusMutation,
} from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { API_BASE_URL } from '../../config/api';

const DealerDetails: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const dealerId = id ? parseInt(id) : null;
    const isValidId = dealerId && !isNaN(dealerId) && dealerId > 0;

    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [verifyAction, setVerifyAction] = useState<'approve' | 'reject'>('approve');
    const [adminNotes, setAdminNotes] = useState('');
    const [newStatus, setNewStatus] = useState<boolean>(true);
    const [selectedDocument, setSelectedDocument] = useState<string | null>(null);

    const { data: dealerResponse, isLoading, error, refetch } = useGetDealerDetailsQuery(dealerId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true,
    });

    // Refetch dealer list when component unmounts (navigating away after approval/rejection)
    useEffect(() => {
        return () => {
            // This will trigger when navigating away from the page
            // The cache invalidation from verifyDealer mutation should handle the refetch
        };
    }, []);

    const { data: documentsResponse } = useGetDealerDocumentsQuery(dealerId!, {
        skip: !isValidId,
    });

    const dealer = dealerResponse?.data;
    const documents = documentsResponse?.data?.documents || [];

    const [verifyDealer, { isLoading: isVerifying }] = useVerifyDealerMutation();
    const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateDealerStatusMutation();

    const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

    const getImageUrl = (url: string | null | undefined) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const cleanUrl = url.startsWith('/') ? url : `/${url}`;
        return `${API_BASE_URL}${cleanUrl}`;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Approved</Badge>;
            case 'pending':
                return <Badge variant="warning" className="flex items-center gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
            case 'rejected':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Rejected</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const handleVerify = (action: 'approve' | 'reject') => {
        setVerifyAction(action);
        setAdminNotes('');
        setShowVerifyModal(true);
    };

    const handleVerifySubmit = async () => {
        if (!dealer) return;

        if (verifyAction === 'reject' && !adminNotes.trim()) {
            toast.error('Please provide a reason for rejection');
            return;
        }

        try {
            await verifyDealer({
                dealer_id: dealer.dealer_id,
                status: verifyAction,
                admin_notes: adminNotes.trim() || undefined,
            }).unwrap();
            toast.success(`Dealer ${verifyAction === 'approve' ? 'approved' : 'rejected'} successfully!`);
            setShowVerifyModal(false);
            setAdminNotes('');
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || `Failed to ${verifyAction} dealer`);
        }
    };

    const handleStatusUpdate = async () => {
        if (!dealer) return;

        try {
            await updateStatus({
                dealer_id: dealer.dealer_id,
                is_active: newStatus,
            }).unwrap();
            toast.success(`Dealer ${newStatus ? 'activated' : 'deactivated'} successfully!`);
            setShowStatusModal(false);
            refetch();
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update dealer status');
        }
    };

    const handleDocumentView = (documentPath: string) => {
        setSelectedDocument(documentPath);
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid dealer ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !dealer) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading dealer. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/dealer-management')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/dealer-management')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Dealer Details</h1>
                                <p className="text-sm text-gray-500">View and manage dealer information</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant={dealer.is_active ? 'danger' : 'success'}
                                onClick={() => {
                                    setNewStatus(!dealer.is_active);
                                    setShowStatusModal(true);
                                }}
                                disabled={isUpdatingStatus}
                            >
                                {dealer.is_active ? (
                                    <>
                                        <XCircle className="w-4 h-4 mr-2" />
                                        Deactivate
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Activate
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Dealer Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Dealer Information</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex items-start space-x-3">
                                    <div className="h-20 w-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                        {dealer.profile_image ? (
                                            <img
                                                src={dealer.profile_image}
                                                alt={dealer.name}
                                                className="h-full w-full object-cover"
                                            />
                                        ) : (
                                            <User className="h-10 w-10 text-gray-400" />
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{dealer.name}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            {getStatusBadge(dealer.verification_status)}
                                            {dealer.is_active ? (
                                                <Badge variant="success" className="flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" />Active
                                                </Badge>
                                            ) : (
                                                <Badge variant="danger" className="flex items-center gap-1">
                                                    <XCircle className="w-3 h-3" />Inactive
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center space-x-2 text-gray-700">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm">{dealer.email}</span>
                                    </div>
                                    {dealer.mobile_number && (
                                        <div className="flex items-center space-x-2 text-gray-700">
                                            <Phone className="w-4 h-4 text-gray-400" />
                                            <span className="text-sm">{dealer.mobile_number}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center space-x-2 text-gray-700">
                                        {dealer.dealer_type === 'car' ? (
                                            <Car className="w-4 h-4 text-gray-400" />
                                        ) : (
                                            <Home className="w-4 h-4 text-gray-400" />
                                        )}
                                        <span className="text-sm capitalize">{dealer.dealer_type} Dealer</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Business Information */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Business Information</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Business Name</label>
                                    <p className="text-gray-900 mt-1 flex items-center">
                                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                                        {dealer.business_name}
                                    </p>
                                </div>
                                {dealer.business_address && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Business Address</label>
                                        <p className="text-gray-900 mt-1 flex items-center">
                                            <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                            {dealer.business_address}
                                        </p>
                                    </div>
                                )}
                                {dealer.business_phone && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Business Phone</label>
                                        <p className="text-gray-900 mt-1 flex items-center">
                                            <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                            {dealer.business_phone}
                                        </p>
                                    </div>
                                )}
                                {dealer.country_code && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Country</label>
                                        <p className="text-gray-900 mt-1">{dealer.country_code}</p>
                                    </div>
                                )}
                                {dealer.currency_symbol && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Currency</label>
                                        <p className="text-gray-900 mt-1">{dealer.currency_symbol}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Documents */}
                        {documents.length > 0 && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-gray-900">Verification Documents</h2>
                                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                        {documents.length} Files
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {documents.map((doc) => {
                                        const docUrl = getImageUrl(doc.document_path);
                                        const isImage = docUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                                        
                                        return (
                                            <div
                                                key={doc.document_id}
                                                className="group relative bg-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-blue-300 transition-all duration-300"
                                            >
                                                {/* Preview Section */}
                                                <div 
                                                    className="aspect-video relative cursor-pointer overflow-hidden bg-gray-200"
                                                    onClick={() => handleDocumentView(docUrl)}
                                                >
                                                    {isImage ? (
                                                        <img
                                                            src={docUrl}
                                                            alt={doc.document_type}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                            onError={(e) => {
                                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x225?text=Preview+Not+Available';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                                            <FileText className="w-12 h-12 mb-2" />
                                                            <span className="text-xs font-medium">Click to View File</span>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Hover Overlay */}
                                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                                                        <div className="bg-white/90 p-2 rounded-full opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 shadow-lg">
                                                            <Eye className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Info Section */}
                                                <div className="p-4 bg-white">
                                                    <div className="flex items-center justify-between">
                                                        <div className="min-w-0 flex-1">
                                                            <h3 className="text-sm font-bold text-gray-900 capitalize truncate mb-0.5">
                                                                {doc.document_type.replace('_', ' ')}
                                                            </h3>
                                                            <p className="text-[10px] text-gray-500 truncate mb-2">
                                                                {doc.document_name}
                                                            </p>
                                                            <div className="flex items-center text-[10px] text-gray-400">
                                                                <Calendar className="w-3 h-3 mr-1" />
                                                                {formatDate(doc.created_at)}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(docUrl, '_blank');
                                                            }}
                                                            className="ml-3 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                                                            title="Download/Full View"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Rejection Reason */}
                        {dealer.verification_status === 'rejected' && dealer.rejection_reason && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                                    <div>
                                        <h3 className="text-sm font-semibold text-red-900 mb-1">Rejection Reason</h3>
                                        <p className="text-sm text-red-700">{dealer.rejection_reason}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Statistics */}
                        {dealer.statistics && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h2 className="text-xl font-semibold text-gray-900 mb-4">Statistics</h2>
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <Store className="w-5 h-5 text-gray-400" />
                                            <span className="text-sm text-gray-600">Total Listings</span>
                                        </div>
                                        <span className="text-lg font-semibold text-gray-900">
                                            {dealer.statistics.total_listings}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <TrendingUp className="w-5 h-5 text-green-400" />
                                            <span className="text-sm text-gray-600">Active Listings</span>
                                        </div>
                                        <span className="text-lg font-semibold text-green-600">
                                            {dealer.statistics.active_listings}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="w-5 h-5 text-blue-400" />
                                            <span className="text-sm text-gray-600">Sold Listings</span>
                                        </div>
                                        <span className="text-lg font-semibold text-blue-600">
                                            {dealer.statistics.sold_listings}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Verification Details */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">Verification Details</h2>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Status</label>
                                    <div className="mt-1">{getStatusBadge(dealer.verification_status)}</div>
                                </div>
                                {dealer.verified_by && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Verified By</label>
                                        <p className="text-gray-900 mt-1">{dealer.verified_by.name}</p>
                                        <p className="text-xs text-gray-500">{dealer.verified_by.email}</p>
                                    </div>
                                )}
                                {dealer.verified_at && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Verified At</label>
                                        <p className="text-gray-900 mt-1 flex items-center">
                                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                            {formatDate(dealer.verified_at)}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Created At</label>
                                    <p className="text-gray-900 mt-1 flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                        {formatDate(dealer.created_at)}
                                    </p>
                                </div>
                                {dealer.updated_at && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Last Updated</label>
                                        <p className="text-gray-900 mt-1 flex items-center">
                                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                            {formatDate(dealer.updated_at)}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Status Update Modal */}
            <Modal
                isOpen={showStatusModal}
                onClose={() => setShowStatusModal(false)}
                title={`${newStatus ? 'Activate' : 'Deactivate'} Dealer`}
            >
                <div className="p-6">
                    <p className="text-gray-700 mb-4">
                        Are you sure you want to {newStatus ? 'activate' : 'deactivate'} the dealer{' '}
                        <span className="font-semibold">{dealer?.name}</span>?
                    </p>
                    <div className="flex justify-end gap-3 mt-6">
                        <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant={newStatus ? 'success' : 'danger'}
                            onClick={handleStatusUpdate}
                            disabled={isUpdatingStatus}
                        >
                            {isUpdatingStatus ? 'Updating...' : `${newStatus ? 'Activate' : 'Deactivate'}`}
                        </Button>
                    </div>
                </div>
            </Modal>

             {/* Document View Modal */}
            {selectedDocument && (
                <Modal
                    isOpen={!!selectedDocument}
                    onClose={() => setSelectedDocument(null)}
                    title="Document Preview"
                >
                    <div className="p-0 overflow-hidden bg-gray-900">
                        <div className="relative flex items-center justify-center min-h-[400px] max-h-[80vh]">
                            <img
                                src={selectedDocument}
                                alt="Document Full View"
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/600x400?text=Document+Not+Available';
                                }}
                            />
                        </div>
                        <div className="bg-white p-4 flex justify-between items-center border-t border-gray-200">
                            <div className="text-sm text-gray-500 font-medium">
                                Document Viewer
                            </div>
                            <div className="flex gap-3">
                                <Button
                                    variant="outline"
                                    onClick={() => setSelectedDocument(null)}
                                >
                                    Close
                                </Button>
                                <a
                                    href={selectedDocument}
                                    download
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-all shadow-md hover:shadow-lg text-sm font-semibold"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Full File
                                </a>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default DealerDetails;

